'use client'

import { noop } from 'lodash'
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react'

import { useURLHashState } from '@src/hooks/useURLHashState'
import { delay } from '@src/utils/delay'

export interface ModalContextType {
  isOpen: (modalId: string) => boolean
  setClose: (modalId: string) => void
  setOpen: (modalId: string) => void
}

const ModalContext = createContext<ModalContextType>({
  isOpen: () => false,
  setClose: noop,
  setOpen: noop,
})

interface ModalProviderProps {
  children: ReactNode
}

const MODAL_HASH_PREFIX = 'modal-'
export const APP_HASH = { cart: 'cart', chat: 'chat' }
export const APP_HASHES = Object.values(APP_HASH)

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [_isOpen, _setOpen] = useState<Record<string, boolean>>({})
  const [hash, setHash] = useURLHashState()

  useEffect(() => {
    _setOpen({})
    const isValidHash = hash.startsWith(MODAL_HASH_PREFIX) || APP_HASHES.includes(hash)
    if (!isValidHash) return
    const isAnotherModalOpen =
      document.querySelector('[data-headlessui-state="open"]') &&
      document.querySelector('[data-headlessui-state="open"]')?.hasAttribute('data-open')
    isAnotherModalOpen
      ? delay(1000).then(() => _setOpen({ [hash]: true }))
      : _setOpen({ [hash]: true })
  }, [hash])

  const set = (modalId: string, val: boolean) => {
    setHash(val ? `${MODAL_HASH_PREFIX}${modalId}` : '')
  }
  const setOpen = (modalId: string) => {
    set(modalId, true)
  }
  const setClose = (modalId: string) => {
    set(modalId, false)
  }
  const isOpen = (modalId: string) => {
    return _isOpen?.[`${MODAL_HASH_PREFIX}${modalId}`] ?? _isOpen?.[modalId] ?? false
  }

  return (
    <ModalContext.Provider
      value={{
        setClose,
        setOpen,
        isOpen,
      }}
    >
      {children}
    </ModalContext.Provider>
  )
}

export const useModal = () => {
  return useContext(ModalContext)
}
